// src/stores/useSettingStore.js
import { defineStore } from "pinia";
import * as settingService from "@/services/settingService";

export const useSettingStore = defineStore("setting", {
  state: () => ({
    // O Setting é um objeto único
    setting: null,
    error: null,
    isLoading: false,
    isLoaded: false,
    // NOVO STATE: Armazena o último objeto Setting consultado/salvo.
    lastConsultedSetting: null,
  }),

  getters: {
    // Exemplo de getter para facilitar o acesso à cor de fundo
    uncategorizedBgColor: (state) =>
      state.setting?.uncategorizedBackgroundColor || "#FFFFFF",
  },

  actions: {
    /**
     * Clona o objeto de configuração atual (se existir) para lastConsultedSetting.
     * Isso é útil para reverter alterações.
     * @param {object | null} currentSetting - O objeto setting atual.
     */
    _setLastConsulted(currentSetting) {
      if (currentSetting) {
        // Usa structuredClone para criar uma cópia profunda, garantindo que
        // a alteração em 'setting' não mude 'lastConsultedSetting'
        this.lastConsultedSetting = structuredClone(currentSetting);
      } else {
        this.lastConsultedSetting = null;
      }
    },

    /**
     * Restaura o 'setting' a partir do 'lastConsultedSetting'.
     * Útil para cancelar edições no frontend.
     */
    restoreSetting() {
      if (this.lastConsultedSetting) {
        this.setting = structuredClone(this.lastConsultedSetting);
        return true;
      }
      return false;
    },

    /**
     * Busca e armazena a configuração do usuário logado.
     */
    async fetchSetting() {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await settingService.getSetting();
        this.setting = response.data;
        this.isLoaded = true;

        // NOVO: Salva a configuração recém-consultada como a última consultada.
        this._setLastConsulted(this.setting);

        return this.setting;
      } catch (error) {
        if (error.response && error.response.status === 404) {
          this.setting = null;
          this.isLoaded = true;
        } else {
          const mensagem = error.response?.data.message || error.message;
          this.error = mensagem;
          console.error("❌ Erro ao buscar configuração:", mensagem);
          throw new Error(mensagem);
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Cria ou atualiza a configuração do usuário.
     * @param {object} data - SettingUpdateDto com os campos a serem alterados.
     */
    async saveSetting(data) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await settingService.createOrUpdateSetting(data);
        this.setting = response.data; // Atualiza o estado com o DTO retornado
        this.isLoaded = true;

        // NOVO: Atualiza a última configuração consultada/salva
        this._setLastConsulted(this.setting);

        return response.data;
      } catch (error) {
        const mensagem = error.response?.data.message || error.message;
        this.error = mensagem;
        console.error("❌ Erro ao salvar configuração:", mensagem);
        throw error; // Lança o erro para o componente tratar a UI
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Exclui a configuração do usuário.
     */
    async deleteSetting() {
      this.isLoading = true;
      this.error = null;
      try {
        await settingService.deleteSetting();
        this.setting = null; // Limpa o estado
        this.isLoaded = false;

        // NOVO: Limpa também o último estado consultado
        this._setLastConsulted(null);
      } catch (error) {
        const mensagem = error.response?.data.message || error.message;
        this.error = mensagem;
        console.error("❌ Erro ao deletar configuração:", mensagem);
        throw new Error(mensagem);
      } finally {
        this.isLoading = false;
      }
    },
  },
});
